/**
 * All projects → one record type: pick a project (`Led AI - Project Scoped
 * Nav.dc.html`, "ALL PROJECTS: pick a project for this record type").
 *
 * With no project in scope, a record type in the sidebar opens this instead
 * of a list that mixes every project — and every system's vocabulary — in
 * one table. The page is the source's projects: totals across them, a search,
 * three sorts and a row per project with its two numbers, the last seven days
 * and when it last moved. A row opens that project's records, scoped.
 *
 * Every number comes from data the console already holds: the record columns
 * fold out of the `/api/items` rows (`projectScope.ts`), walks and photos out
 * of that response's `siteActivity`.
 */

import { useMemo, useState } from "react";
import type { ConsoleRow, ConsoleSite } from "../api";
import type { SiteActivity } from "../../server/queries/siteActivity";
import { ASK_COMPOSER_CLEARANCE } from "../components/chrome";
import { DsIcon } from "../components/DsIcon";
import { DS, FONT_MONO, FONT_TEXT, RT } from "../tokens";
import {
  agoLabel,
  columnsFor,
  projectStat,
  sortProjects,
  SOURCE_LABEL,
  sourceOf,
  typeLabel,
  type ProjectSort,
  type RecordSource,
  type ScopedType,
} from "../shell/projectScope";
import { SourceMark } from "./ProjectDetails";

const GRID = "minmax(200px,1.6fr) 80px 96px 96px 120px 20px";

export function ProjectsForType({
  source,
  type,
  sites,
  rows,
  activity,
  hrefForProject,
  onOpenProject,
}: {
  source: RecordSource;
  type: ScopedType;
  sites: ConsoleSite[];
  rows: ConsoleRow[];
  activity: Record<string, SiteActivity> | undefined;
  hrefForProject: (siteId: string) => string;
  onOpenProject: (siteId: string) => void;
}) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<ProjectSort>("most");
  const label = typeLabel(source, type);
  const cols = columnsFor(type);
  const mine = useMemo(() => sites.filter((site) => sourceOf(site) === source), [sites, source]);
  const today = useMemo(() => new Date(), [rows, activity]);
  const stats = useMemo(() => mine.map((site) => projectStat(site, type, rows, activity?.[site.id], today)), [mine, type, rows, activity, today]);
  const needle = q.trim().toLowerCase();
  const shown = sortProjects(stats.filter((stat) => !needle || stat.site.name.toLowerCase().includes(needle)), sort);
  const sum = (pick: (stat: (typeof stats)[number]) => number) => stats.reduce((total, stat) => total + pick(stat), 0);
  const weekAgo = today.getTime() - 7 * 86_400_000;
  const totals = [
    { k: `${cols.a} · all projects`, v: sum((stat) => stat.a), red: false },
    { k: cols.b, v: sum((stat) => stat.b), red: cols.bIsOverdue },
    { k: "New this week", v: sum((stat) => stat.week.reduce((total, day) => total + day, 0)), red: false },
    { k: "Projects active this week", v: stats.filter((stat) => (stat.last?.getTime() ?? 0) >= weekAgo).length, red: false },
  ];
  const noun = `${SOURCE_LABEL[source]} project${mine.length === 1 ? "" : "s"}`;

  return (
    <div data-testid="projects-for-type" data-source={source} data-type={type} className="rt-noscroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", fontFamily: FONT_TEXT, color: DS.ink }}>
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: `40px 32px var(--ask-composer-clearance, ${ASK_COMPOSER_CLEARANCE}px)` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <SourceMark source={source === "led" ? undefined : source} size={14} />
          <span style={EYEBROW}>{`${SOURCE_LABEL[source]} · ${mine.length} ${mine.length === 1 ? "project" : "projects"}`}</span>
        </div>
        <h1 data-testid="projects-for-type-title" style={{ margin: "8px 0 0", fontSize: 32, fontWeight: 800, letterSpacing: "-1px" }}>{label}</h1>
        <p style={{ margin: "6px 0 0", fontSize: 15, color: DS.body2 }}>{`Pick a project to see its ${label.toLowerCase()}.`}</p>

        <div data-testid="projects-for-type-totals" style={{ display: "flex", marginTop: 22, padding: "14px 0", borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }}>
          {totals.map((total, index) => (
            <div key={total.k} style={{ flex: 1, minWidth: 0, padding: "0 18px", borderLeft: index ? `1px solid ${LINE}` : "none" }}>
              <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.8px", color: total.red && total.v ? RT.overdue : DS.ink }}>{total.v}</div>
              <div style={{ ...EYEBROW, fontSize: 9.5, letterSpacing: "1.3px", marginTop: 2 }}>{total.k}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
          <label style={{ flex: 1, minWidth: 240, display: "flex", alignItems: "center", gap: 9, height: 40, padding: "0 13px", borderRadius: 10, border: "1px solid rgba(10,10,10,0.13)", background: RT.pane }}>
            <DsIcon name="search" size={14} color={DS.meta} />
            <input
              className="rt-input"
              data-testid="projects-for-type-search"
              value={q}
              onInput={(event) => setQ(event.currentTarget.value)}
              placeholder={`Search ${mine.length} ${mine.length === 1 ? "project" : "projects"}`}
              aria-label="Search projects"
              style={{ flex: 1, minWidth: 0, border: 0, outline: "none", background: "transparent", font: "inherit", fontSize: 14, color: DS.ink }}
            />
          </label>
          <div role="tablist" aria-label="Sort" style={{ display: "flex", gap: 2, padding: 3, borderRadius: 10, background: DS.pickerOverflow }}>
            {([["most", `Most ${cols.a.toLowerCase()}`], ["recent", "Recent activity"], ["az", "A–Z"]] as const).map(([id, text]) => {
              const on = sort === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  data-testid={`projects-for-type-sort-${id}`}
                  onClick={() => setSort(id)}
                  style={{ height: 32, padding: "0 12px", borderRadius: 8, border: "none", background: on ? RT.pane : "transparent", boxShadow: on ? "0 1px 3px rgba(10,10,10,0.1)" : "none", font: "inherit", fontSize: 13, fontWeight: 600, color: on ? DS.ink : DS.body2, cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  {text}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rt-noscroll" style={{ marginTop: 12, border: `1px solid ${LINE}`, borderRadius: 14, background: RT.pane, overflowX: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: GRID, minWidth: 680, gap: 12, padding: "10px 18px", background: RT.strip, borderBottom: `1px solid ${LINE}`, ...EYEBROW, fontSize: 9.5, letterSpacing: "1.3px" }}>
            <span>Project</span><span>{cols.a}</span><span>{cols.b}</span><span>This week</span><span>Last activity</span><span />
          </div>
          {shown.map((stat, index) => {
            const secondary = [
              stat.walks !== undefined ? `${stat.walks} ${stat.walks === 1 ? "walk" : "walks"}` : null,
              stat.photos !== undefined ? `${stat.photos} ${stat.photos === 1 ? "photo" : "photos"}` : null,
            ].filter(Boolean).join(" · ");
            return (
              <a
                key={stat.site.id}
                href={hrefForProject(stat.site.id)}
                data-testid="projects-for-type-row"
                data-site={stat.site.id}
                className="rt-proj-row"
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                  event.preventDefault();
                  onOpenProject(stat.site.id);
                }}
                style={{ display: "grid", gridTemplateColumns: GRID, minWidth: 680, gap: 12, alignItems: "center", padding: "13px 18px", borderTop: index ? "1px solid rgba(10,10,10,0.06)" : "none", color: DS.ink, textDecoration: "none", cursor: "pointer" }}
              >
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 14.5, fontWeight: 700, letterSpacing: "-0.2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{stat.site.name || "Untitled project"}</span>
                  {secondary && <span style={{ display: "block", fontSize: 12, color: DS.meta, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{secondary}</span>}
                </span>
                <span data-testid="projects-for-type-a" style={{ fontSize: 14, fontWeight: 700 }}>{stat.a}</span>
                <span data-testid="projects-for-type-b" style={{ fontSize: 14, fontWeight: 600, color: cols.bIsOverdue && stat.b ? RT.overdue : RT.cell }}>{stat.b}</span>
                <span aria-label={`${stat.week.reduce((total, day) => total + day, 0)} this week`} style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 20 }}>
                  {stat.week.map((value, day) => (
                    <span key={day} style={{ width: 6, height: 3 + Math.min(value, 5) * 3, borderRadius: 1.5, background: value ? DS.ink : RT.barOff, display: "block" }} />
                  ))}
                </span>
                <span style={{ fontSize: 13, color: DS.body2, whiteSpace: "nowrap" }}>{agoLabel(stat.last, today)}</span>
                <DsIcon name="chevron" size={13} color={DS.dashed} />
              </a>
            );
          })}
          {shown.length === 0 && (
            <div style={{ padding: "22px 18px", fontSize: 13.5, color: DS.body2 }}>{needle ? `No ${SOURCE_LABEL[source]} project matches “${q.trim()}”.` : `No ${noun} in this workspace.`}</div>
          )}
          <div data-testid="projects-for-type-foot" style={{ minWidth: 680, padding: "11px 18px", borderTop: "1px solid rgba(10,10,10,0.06)", background: RT.strip, ...EYEBROW, fontWeight: 400, fontSize: 10, letterSpacing: "1px" }}>
            {`Showing ${shown.length} of ${mine.length} ${noun} · search to find any`}
          </div>
        </div>
      </div>
    </div>
  );
}

const LINE = "rgba(10,10,10,0.08)";

const EYEBROW = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "1.4px",
  textTransform: "uppercase" as const,
  color: DS.meta,
};
